import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeftIcon, TrashIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/ApiService';
import toast from 'react-hot-toast';
import { useConnectionAwareSubmit } from '../../hooks/useConnectionAwareSubmit';
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import { US_CITIES, US_STATES } from '../../utils/usCitiesStates.js';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing.lg};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.text.primary};
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  
  &:hover {
    background: ${props => props.theme.colors.surface};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const Title = styled.h1`
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.h2.fontSize};
  font-weight: ${props => props.theme.typography.h2.fontWeight};
`;

const DeleteButton = styled.button`
  background: ${props => props.theme.colors.error};
  color: white;
  border: none;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.errorHover || '#DC2626'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Form = styled.form`
  max-width: 600px;
  margin: 0 auto;
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.text.primary};
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.body.fontSize};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.body.fontSize};
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${props => props.theme.spacing.xl};
`;

const Button = styled.button`
  background: ${props => props.$primary ? props.theme.colors.gradient : 'transparent'};
  color: ${props => props.$primary ? 'white' : props.theme.colors.text.primary};
  border: 1px solid ${props => props.$primary ? props.theme.colors.accent : props.theme.colors.border};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$primary ? props.theme.colors.gradientHover : props.theme.colors.surface};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.text.secondary};
`;

const genres = [
  'Rock', 'Indie Rock', 'Alternative Rock', 'Classic Rock', 'Progressive Rock', 'Post-Rock', 'Shoegaze',
  'Metal', 'Death Metal', 'Black Metal', 'Doom Metal', 'Thrash Metal', 'Heavy Metal', 'Hardcore',
  'Grindcore', 'Metalcore', 'Deathcore', 'Post-Hardcore',
  'Punk', 'Pop Punk', 'Hardcore Punk', 'Post-Punk', 'Punk Rock',
  'Electronic', 'House', 'Techno', 'Dubstep', 'Drum & Bass', 'Ambient', 'IDM', 'Trance', 'EDM',
  'Synth-pop', 'Synthwave', 'Experimental Electronic',
  'Hip Hop', 'Rap', 'Trap', 'Underground Hip Hop', 'Conscious Rap',
  'Folk', 'Acoustic', 'Singer-Songwriter', 'Indie Folk', 'Folk Rock', 'Americana',
  'Country', 'Alt-Country', 'Bluegrass', 'Country Rock', 'Outlaw Country',
  'Jazz', 'Blues', 'Blues Rock', 'Jazz Fusion', 'Swing', 'Bebop',
  'R&B', 'Soul', 'Funk', 'Neo-Soul', 'Motown',
  'Reggae', 'Ska', 'Ska Punk', 'Dub', 'Dancehall',
  'Pop', 'Indie Pop', 'Dream Pop', 'Electropop', 'Art Pop',
  'Classical', 'Experimental', 'Noise', 'Avant-garde', 'Other'
];

const Genre = styled.span`
  display: inline-flex;
  align-items: center;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.small.fontSize};
  font-weight: 500;
  margin-right: ${props => props.theme.spacing.xs};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const BandsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const BandInput = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  align-items: center;
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.body.fontSize};
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }
`;

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#fff',
    color: '#222',
    borderColor: state.isFocused ? '#8B5CF6' : '#ccc',
    boxShadow: state.isFocused ? '0 0 0 2px #8B5CF633' : provided.boxShadow,
    minHeight: 40,
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#fff',
    color: '#222',
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? '#8B5CF622' : '#fff',
    color: '#222',
    cursor: 'pointer',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#222',
  }),
  input: (provided) => ({
    ...provided,
    color: '#222',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#888',
  }),
};

const EditShow = () => {
  const { showId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { submitWithConnectionCheck, isSubmitting } = useConnectionAwareSubmit();

  const [show, setShow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bands, setBands] = useState(['']);
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [formData, setFormData] = useState({
    venue_name: '',
    venue_address: '',
    city: '',
    state: '',
    show_date: '',
    show_time: '',
    description: '',
    ticket_price: '',
    ticket_url: '',
    age_limit: 'all-ages'
  });
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedState, setSelectedState] = useState(null);

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }

    const fetchShow = async () => {
      try {
        const showData = await apiService.getShowById(showId);
        if (showData.poster_id !== currentUser.id) {
          toast.error('You can only edit your own shows');
          navigate('/my-shows');
          return;
        }
        setShow(showData);
        // Bands
        if (showData.band_name) {
          const bandArray = showData.band_name.split(' & ').map(band => band.trim()).filter(band => band);
          setBands(bandArray.length > 0 ? bandArray : ['']);
        }
        // Genres
        let genresFromDesc = [];
        if (showData.description) {
          const genreMatch = showData.description.match(/\[GENRES:(.*?)\]/);
          if (genreMatch) {
            genresFromDesc = genreMatch[1].split(',').map(g => g.trim()).filter(g => g);
          }
        }
        if (showData.genre && !genresFromDesc.length) {
          genresFromDesc = [showData.genre];
        }
        setSelectedGenres(genresFromDesc.length ? genresFromDesc : []);
        // Description (remove [GENRES:...] tag)
        let cleanDescription = showData.description || '';
        cleanDescription = cleanDescription.replace(/\n?\n?\[GENRES:.*?\]/, '').trim();
        setFormData({
          venue_name: showData.venue_name || '',
          venue_address: showData.venue_address || '',
          city: showData.city || '',
          state: showData.state || '',
          show_date: showData.show_date || '',
          show_time: showData.show_time || '',
          description: cleanDescription,
          ticket_price: showData.ticket_price || '',
          ticket_url: showData.ticket_url || '',
          age_limit: showData.age_limit || 'all-ages'
        });
        // Prepopulate react-select for city/state
        let cityOption = US_CITIES.find(opt => opt.value.toLowerCase() === (showData.city || '').toLowerCase());
        if (!cityOption && showData.city) {
          cityOption = { value: showData.city, label: showData.city };
        }
        setSelectedCity(cityOption || null);
        const stateOption = US_STATES.find(opt => opt.value === (showData.state || ''));
        setSelectedState(stateOption || null);
      } catch (error) {
        console.error('Error fetching show:', error);
        toast.error('Failed to load show details');
        navigate('/my-shows');
      } finally {
        setLoading(false);
      }
    };
    fetchShow();
  }, [showId, currentUser, navigate]);

  // Band input handlers
  const addBand = () => setBands([...bands, '']);
  const removeBand = (index) => setBands(bands.filter((_, i) => i !== index));
  const updateBand = (index, value) => {
    const newBands = [...bands];
    newBands[index] = value;
    setBands(newBands);
  };

  // Genre handlers
  const addGenre = (genre) => {
    if (selectedGenres.length < 3 && !selectedGenres.includes(genre)) {
      setSelectedGenres([...selectedGenres, genre]);
    }
  };
  const removeGenre = (genre) => {
    setSelectedGenres(selectedGenres.filter(g => g !== genre));
  };

  // Age limit options
  const ageLimitOptions = [
    { value: 'all-ages', label: 'All Ages' },
    { value: '18+', label: '18+' },
    { value: '21+', label: '21+' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validBands = bands.map(band => band.trim()).filter(band => band.length >= 1);
    if (validBands.length === 0) {
      toast.error('Please add at least one band (min 1 character each)');
      return;
    }
    if (selectedGenres.length === 0) {
      toast.error('Please select at least one genre');
      return;
    }
    // Clean up form data - convert empty strings to null for optional fields
    const city = selectedCity ? selectedCity.value : formData.city.trim();
    const state = selectedState ? selectedState.value : formData.state.trim().toUpperCase();
    const cleanedFormData = {
      ...formData,
      band_name: validBands.join(' & '),
      ticket_url: formData.ticket_url?.trim() || null,
      ticket_price: formData.ticket_price?.trim() || null,
      age_limit: formData.age_limit?.trim() || null,
      description: `${formData.description || ''}\n\n[GENRES:${selectedGenres.join(',')}]`,
      venue_address: formData.venue_address?.trim() || null,
      show_time: formData.show_time?.trim() || null,
      city: city,
      state: state
    };
    const updateFunction = async () => {
      const result = await apiService.updateShow(showId, cleanedFormData);
      if (!result.success) {
        throw new Error(result.error || 'Failed to update show');
      }
      return { success: true };
    };
    const result = await submitWithConnectionCheck(updateFunction, {
      loadingMessage: 'Updating show...',
      successMessage: 'Show updated successfully!',
      errorMessage: 'Failed to update show',
      offlineMessage: 'No internet connection. Show will be updated when connection is restored...',
      retryMessage: 'Connection restored! Updating show...'
    });
    if (result?.success) {
      navigate('/my-shows');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this show? This action cannot be undone.')) {
      return;
    }
    const deleteFunction = async () => {
      const result = await apiService.deleteShow(showId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete show');
      }
      return { success: true };
    };
    const result = await submitWithConnectionCheck(deleteFunction, {
      loadingMessage: 'Deleting show...',
      successMessage: 'Show deleted successfully!',
      errorMessage: 'Failed to delete show',
      offlineMessage: 'No internet connection. Show will be deleted when connection is restored...',
      retryMessage: 'Connection restored! Deleting show...'
    });
    if (result?.success) {
      navigate('/my-shows');
    }
  };

  if (!currentUser) return null;
  if (loading) {
    return (
      <Container>
        <LoadingSpinner>Loading show details...</LoadingSpinner>
      </Container>
    );
  }
  if (!show) {
    return (
      <Container>
        <LoadingSpinner>Show not found</LoadingSpinner>
      </Container>
    );
  }
  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/my-shows')}>
            <ArrowLeftIcon />
          </BackButton>
          <Title>Edit Show</Title>
        </HeaderLeft>
        <DeleteButton onClick={handleDelete} disabled={isSubmitting}>
          <TrashIcon />
          Delete Show
        </DeleteButton>
      </Header>
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Bands/Artists *</Label>
          <BandsList>
            {bands.map((band, index) => (
              <BandInput key={index}>
                <Input
                  type="text"
                  value={band}
                  onChange={e => updateBand(index, e.target.value)}
                  placeholder={`Band ${index + 1}`}
                  required
                />
                {bands.length > 1 && (
                  <button type="button" onClick={() => removeBand(index)} style={{ background: 'none', border: 'none', color: '#DC2626', marginLeft: 4, cursor: 'pointer' }}>
                    <XMarkIcon style={{ width: 16, height: 16 }} />
                  </button>
                )}
              </BandInput>
            ))}
          </BandsList>
          <button type="button" onClick={addBand} style={{ marginTop: 8, background: 'none', border: '1px solid #8B5CF6', color: '#8B5CF6', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
            <PlusIcon style={{ width: 16, height: 16 }} /> Add Band
          </button>
        </FormGroup>
        <FormGroup>
          <Label>Genres (Select up to 3)</Label>
          <div style={{ marginBottom: '8px' }}>
            {selectedGenres.map((genre) => (
              <Genre key={genre}>
                {genre}
                <button
                  type="button"
                  onClick={() => removeGenre(genre)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    marginLeft: '8px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  ×
                </button>
              </Genre>
            ))}
          </div>
          <select
            value=""
            onChange={e => {
              if (e.target.value) {
                addGenre(e.target.value);
                e.target.value = '';
              }
            }}
            disabled={selectedGenres.length >= 3}
            style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', minWidth: 180 }}
          >
            <option value="">
              {selectedGenres.length >= 3 ? 'Maximum 3 genres selected' : 'Add a genre...'}
            </option>
            {genres.filter(genre => !selectedGenres.includes(genre)).map(genre => (
              <option key={genre} value={genre}>{genre}</option>
            ))}
          </select>
        </FormGroup>
        <FormGroup>
          <Label htmlFor="description">Description</Label>
          <TextArea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Tell people about this show..."
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="venue_name">Venue Name *</Label>
          <Input
            type="text"
            id="venue_name"
            name="venue_name"
            value={formData.venue_name}
            onChange={handleInputChange}
            required
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="venue_address">Venue Address</Label>
          <Input
            type="text"
            id="venue_address"
            name="venue_address"
            value={formData.venue_address}
            onChange={handleInputChange}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="city">City *</Label>
          <CreatableSelect
            options={US_CITIES}
            value={selectedCity}
            onChange={option => {
              setSelectedCity(option);
              setFormData(prev => ({ ...prev, city: option ? option.value : '' }));
            }}
            placeholder="Select or type your city..."
            isClearable
            styles={customSelectStyles}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="state">State *</Label>
          <Select
            options={US_STATES}
            value={selectedState}
            onChange={option => {
              setSelectedState(option);
              setFormData(prev => ({ ...prev, state: option ? option.value : '' }));
            }}
            placeholder="Select your state..."
            isClearable
            styles={customSelectStyles}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="show_date">Show Date *</Label>
          <Input
            type="date"
            id="show_date"
            name="show_date"
            value={formData.show_date}
            onChange={handleInputChange}
            required
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="show_time">Show Time</Label>
          <Input
            type="time"
            id="show_time"
            name="show_time"
            value={formData.show_time}
            onChange={handleInputChange}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="ticket_price">Ticket Price</Label>
          <Input
            type="text"
            id="ticket_price"
            name="ticket_price"
            value={formData.ticket_price}
            onChange={handleInputChange}
            placeholder="e.g., $15, Free, $10-20"
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="ticket_url">Ticket URL</Label>
          <Input
            type="url"
            id="ticket_url"
            name="ticket_url"
            value={formData.ticket_url}
            onChange={handleInputChange}
            placeholder="https://..."
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="age_limit">Age Limit</Label>
          <select
            id="age_limit"
            name="age_limit"
            value={formData.age_limit}
            onChange={handleInputChange}
            style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', minWidth: 120 }}
          >
            {ageLimitOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </FormGroup>
        <ButtonGroup>
          <Button type="button" onClick={() => navigate('/my-shows')}>Cancel</Button>
          <Button type="submit" $primary disabled={isSubmitting}>
            {isSubmitting ? 'Updating...' : 'Update Show'}
          </Button>
        </ButtonGroup>
      </Form>
    </Container>
  );
};

export default EditShow; 